/**
 * Entry point.
 *
 * The font and the theme tokens are imported here rather than in App, so they
 * load before any component's stylesheet and a component can be deleted without
 * taking the palette with it.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import "@fontsource-variable/plus-jakarta-sans";
import "./styles/theme.css";
import App from "./App";
import { SettingsProvider } from "./features/settings/SettingsContext";
import { hydrateItems } from "./lib/storage/itemStore";
import { migrateFromLocalStorage } from "./lib/storage/migrate";
import { hydrateRecognitionCache } from "./features/vision/recognitionCache";

const container: HTMLElement | null = document.getElementById("root");
if (!container) throw new Error("index.html is missing #root");
const root = container;

/**
 * Storage is opened *before* the first render, not during it.
 *
 * The inventory lives in IndexedDB, which is asynchronous, while the app and
 * its tests read it synchronously. Both are true because the async part happens
 * exactly once, here: migrate anything left in localStorage, read the database
 * into memory, and only then render. After that `loadFoodItems()` is a memory
 * read, as it always was.
 *
 * Rendering after the read rather than before it avoids a frame of empty
 * kitchen — "you have no food" is a bad thing to say to someone for 40ms, and a
 * worse thing if they tap something in that frame.
 *
 * Settings deliberately do **not** wait: they stay in localStorage precisely so
 * the theme is readable synchronously, because a theme fetched asynchronously
 * is a white flash on every load.
 *
 * Nothing here can reject. Private-browsing modes refuse IndexedDB outright,
 * and an app that won't start because it couldn't reach its cache is a far
 * worse failure than one that starts empty.
 */
async function start(): Promise<void> {
  await migrateFromLocalStorage();
  await Promise.all([hydrateItems(), hydrateRecognitionCache()]);

  // StrictMode double-invokes effects in development on purpose, which would
  // fire recognition twice per photo. recogniseFood de-duplicates by photo
  // hash, so the second effect joins the first request rather than paying for
  // another one.
  createRoot(root).render(
    <StrictMode>
      <SettingsProvider>
        <App />
      </SettingsProvider>
    </StrictMode>
  );
}

void start();
